import { useEffect, useState } from 'react';
import vopayApi from '@/services/api.config';
import useStorage from '../useStorage';
import { usePermissions } from '@/providers/Permissions.provider';

const { api } = vopayApi;

const CACHE_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours

const tokenCache = new Map<string, { token: string; fetchedAt: number }>();
const inFlight = new Map<string, Promise<string | undefined>>();
async function findPartnerToken(loggedInAccountId: string, partnerAccountId: string): Promise<string | undefined> {
  const cacheKey = `${loggedInAccountId}::${partnerAccountId}`;

  const cached = tokenCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) return cached.token;

  const existing = inFlight.get(cacheKey);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const res = await api.fetchPartnerPartners({ ShowToken: true, SearchText: partnerAccountId });
      if (!res.Success) throw new Error(res.ErrorMessage);
      const token = res.Partners.find((partner) => partner.PartnerAccountID === partnerAccountId)?.Token;
      if (token) tokenCache.set(cacheKey, { token, fetchedAt: Date.now() });
      return token;
    } finally {
      inFlight.delete(cacheKey);
    }
  })();

  inFlight.set(cacheKey, promise);
  return promise;
}

/**
 * Bearer token for the partner identified by partnerAccountId: the session token when it's the
 * current (logged-in) partner, otherwise the matching partner's token (cached 3h per partner).
 */
const usePartnerBearer = (partnerAccountId: string): string => {
  const {
    login: { getPartnerLogin },
  } = useStorage();
  const { permissions } = usePermissions();
  const { accountId, JWT } = getPartnerLogin();

  const useSessionToken = !permissions.PartnerCreationEnabled || !partnerAccountId || partnerAccountId === accountId;
  const [token, setToken] = useState(useSessionToken ? JWT : '');

  useEffect(() => {
    if (useSessionToken) {
      setToken(JWT);
      return;
    }

    let active = true;

    const resolve = async () => {
      try {
        const resolved = await findPartnerToken(accountId, partnerAccountId);
        if (active) setToken(resolved ?? '');
      } catch (error) {
        console.error(error);
        if (active) setToken('');
      }
    };

    void resolve();

    return () => {
      active = false;
    };
  }, [useSessionToken, partnerAccountId, JWT, accountId]);

  return token;
};

export default usePartnerBearer;
